import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { getUserDetails } from "../../components/content/axiosConfig";
import Modal from "react-modal";
import Swal from 'sweetalert2';
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { useInvalidateCache } from "../../hooks/queries/useInvalidateCache";

const fishTypeModalStyles = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  content: {
    position: "relative",
    inset: "unset",
    transform: "none",
    width: "min(720px, 100%)",
    maxHeight: "min(90vh, 880px)",
    margin: 0,
    padding: 0,
    border: "none",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
};

const PSD_ROWS = [
  ["PsdQualityIn", "PsdQualityCm", "Quality"],
  ["PsdPreferredIn", "PsdPreferredCm", "Preferred"],
  ["PsdMemorableIn", "PsdMemorableCm", "Memorable"],
  ["PsdTrophyIn", "PsdTrophyCm", "Trophy"],
];

const FishTypes = () => {
  const fisherID = getUserDetails()?.FisherID;
  const { invalidateFishTypes } = useInvalidateCache();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFishTypesPage = useCallback(
    ({ search, page, limit }) =>
      axiosInstance.get(`/api/FishTypes/GetAllFishTypes`, {
        params: { search, page, limit },
      }),
    []
  );

  const {
    rows: fishTypes,
    totalCount,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch: refetchFishTypes,
  } = useServerPaginatedList(fetchFishTypesPage);
  const [modalData, setModalData] = useState({
    Name: "",
    Abbreviation: "",
    WsIP: "",
    WsMG: "",
    PsdQualityIn: "",
    PsdQualityCm: "",
    PsdPreferredIn: "",
    PsdPreferredCm: "",
    PsdMemorableIn: "",
    PsdMemorableCm: "",
    PsdTrophyIn: "",
    PsdTrophyCm: "",
    PsdReference: "",
    Status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const DeleteUser = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        setIsSubmitting(true);
        await axiosInstance.delete(`/api/FishTypes/DeleteFishType/${id}`, {
          params:
            fisherID != null && fisherID !== ""
              ? { FisherID: fisherID }
              : {},
        });
        await refetchFishTypes();
        invalidateFishTypes();
        showSuccessToast("Fish type deleted successfully");
        setIsSubmitting(false);
      } catch (error) {
        setIsSubmitting(false);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          window.location.reload();
        }
        const errorMessage = error.response?.data?.message || "An error occurred.";
        showErrorToast(errorMessage);
        console.error("Error managing fish types:", error);
      }
    }
  };

  const openModal = (data = null) => {
    if (data) {
      setIsEditing(true);
      setEditId(data.id);
      setModalData(data);
    } else {
      setIsEditing(false);
      setEditId(null);
      setModalData({
        Name: "",
        Abbreviation: "",
        WsIP: "",
        WsMG: "",
        PsdQualityIn: "",
        PsdQualityCm: "",
        PsdPreferredIn: "",
        PsdPreferredCm: "",
        PsdMemorableIn: "",
        PsdMemorableCm: "",
        PsdTrophyIn: "",
        PsdTrophyCm: "",
        PsdReference: "",
        Status: true
      });
    }
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleChange = (e) => {
    const { id, name, value, type, checked } = e.target;

    if (type === "radio") {
      setModalData({
        ...modalData,
        [name]: value === "true",
      });
    } else {
      setModalData({
        ...modalData,
        [id]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsePsdValue = (value) => {
      if (value === "" || value === null || value === undefined) return null;
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return NaN;
      return parsed;
    };
    const checkPsdOrder = (q, p, m, t, unit) => {
      const values = [q, p, m, t].map(parsePsdValue);
      const hasAny = values.some((v) => v !== null);
      const hasAll = values.every((v) => v !== null);
      if (values.some(Number.isNaN)) {
        return `All PSD ${unit} values must be valid numbers`;
      }
      if (values.some((v) => v !== null && v <= 0)) {
        return `All PSD ${unit} values must be greater than 0`;
      }
      if (hasAny && !hasAll) {
        return `All PSD ${unit} fields are required when any ${unit} value is provided`;
      }
      if (hasAll && !(values[0] < values[1] && values[1] < values[2] && values[2] < values[3])) {
        return `PSD ${unit} thresholds must satisfy Quality < Preferred < Memorable < Trophy`;
      }
      return null;
    };

    // Validation
    if (!modalData.Name.trim()) {
      showErrorToast("Name is required");
      return;
    }
    if (!modalData.WsIP.trim()) {
      showErrorToast("WsIP is required");
      return;
    }

    if (!modalData.WsMG.trim()) {
      showErrorToast("WsMG is required");
      return;
    }
    const inError = checkPsdOrder(
      modalData.PsdQualityIn,
      modalData.PsdPreferredIn,
      modalData.PsdMemorableIn,
      modalData.PsdTrophyIn,
      "inches"
    );
    if (inError) {
      showErrorToast(inError);
      return;
    }
    const cmError = checkPsdOrder(
      modalData.PsdQualityCm,
      modalData.PsdPreferredCm,
      modalData.PsdMemorableCm,
      modalData.PsdTrophyCm,
      "cm"
    );
    if (cmError) {
      showErrorToast(cmError);
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...modalData,
        ...(fisherID != null && fisherID !== "" ? { FisherID: fisherID } : {}),
      };
      if (isEditing) {
        await axiosInstance.put(`/api/FishTypes/UpdateFishType/${editId}`, payload);
        showSuccessToast("Fish type updated successfully");
      } else {
        await axiosInstance.post(`/api/FishTypes/AddFishType`, payload);
        showSuccessToast("Fish type added successfully");
      }
      await refetchFishTypes();
      invalidateFishTypes();
      closeModal();
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        window.location.reload();
      }
      const errorMessage = error.response?.data?.message || "An error occurred.";
      showErrorToast(errorMessage);
      console.error("Error managing fish types:", error);
    }
  };

  return (
    isLoading && fishTypes.length === 0 ? <Preloader /> :
      <div>
        <div className="d-flex align-items-center mb-3">
          <div>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item active">Fish Types</li>
            </ul>
            <h1 className="page-header mb-0">Fish Types</h1>
          </div>
          <div className="ms-auto">
            <button
              type="button"
              className="btn btn-success btn-rounded px-4 rounded-pill"
              onClick={() => openModal()}
              aria-label="Add fish type"
              data-testid="add-fish-type"
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900" aria-hidden="true"></i>
              Add
            </button>
          </div>
        </div>
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              <div className="input-group mb-3">
                <div className="flex-fill position-relative">
                  <div className="input-group">
                    <div
                      className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                      style={{ zIndex: 10 }}
                    >
                      <i className="fa fa-search opacity-5"></i>
                    </div>
                    <input
                      type="text"
                      className="form-control px-35px bg-light"
                      placeholder="Search Fish..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="table-responsive mb-3">
                <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Abbrev.</th>
                      <th>Ws Inch & Pond</th>
                      <th>Ws MM & Gram</th>
                      <th>PSD (in)</th>
                      <th>PSD (cm)</th>
                      <th>Reference</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fishTypes.map((web) => (
                      <tr key={web.id}>
                        <td>{web.Name}</td>
                        <td>{web.Abbreviation || "-"}</td>
                        <td>{web.WsIP}</td>
                        <td>{web.WsMG}</td>
                        <td>{[web.PsdQualityIn, web.PsdPreferredIn, web.PsdMemorableIn, web.PsdTrophyIn].every(v => v !== null && v !== undefined) ? `${web.PsdQualityIn}/${web.PsdPreferredIn}/${web.PsdMemorableIn}/${web.PsdTrophyIn}` : "-"}</td>
                        <td>{[web.PsdQualityCm, web.PsdPreferredCm, web.PsdMemorableCm, web.PsdTrophyCm].every(v => v !== null && v !== undefined) ? `${web.PsdQualityCm}/${web.PsdPreferredCm}/${web.PsdMemorableCm}/${web.PsdTrophyCm}` : "-"}</td>
                        <td>{web.PsdReference || "-"}</td>
                        <td>
                          {web.Status ? (
                            <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                              Active
                            </span>
                          ) : (
                            <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                              Pending
                            </span>
                          )}
                        </td>
                        <td>
                          <span onClick={() => openModal(web)}>
                            <i className="fa fa-pencil fs-13px fa-fw me-5px"></i>
                          </span>
                          &nbsp;&nbsp;

                          <span>
                            <i
                              onClick={() => DeleteUser(web.id)}
                              className="fa fa-trash fs-13px fa-fw me-5px"
                            ></i>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPagination
                currentPage={currentPage}
                totalCount={totalCount}
                entriesPerPage={entriesPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>

        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          style={fishTypeModalStyles}
          contentLabel={isEditing ? "Edit fish type" : "Add fish type"}
        >
          <form onSubmit={handleSubmit} className="d-flex flex-column h-100" style={{ maxHeight: "min(90vh, 880px)" }}>
            <div
              className="d-flex align-items-start gap-3 px-4 py-3 border-bottom bg-white"
              style={{ flexShrink: 0 }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-3 text-white flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
                }}
                aria-hidden
              >
                <i className="fas fa-fish" />
              </span>
              <div className="flex-grow-1 min-w-0">
                <h2 className="h5 mb-0 fw-semibold text-dark">
                  {isEditing ? "Edit Fish Type" : "Add Fish Type"}
                </h2>
                <p className="text-muted small mb-0 mt-1">
                  {isEditing
                    ? "Update species name, weight formulas, and PSD thresholds."
                    : "Define a new species with weight-length formulas and optional PSD values."}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-link text-muted p-2 flex-shrink-0"
                onClick={closeModal}
                aria-label="Close"
              >
                <i className="fas fa-times fa-lg" />
              </button>
            </div>

            <div className="px-4 py-3 overflow-auto bg-light flex-grow-1">
              <section className="bg-white rounded-3 border p-3 mb-3">
                <h3 className="h6 text-uppercase text-muted fw-semibold mb-3" style={{ letterSpacing: "0.04em", fontSize: "0.7rem" }}>
                  Basic
                </h3>
                <label htmlFor="Name" className="form-label small fw-medium mb-1">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Black Crappie"
                  id="Name"
                  name="Name"
                  value={modalData.Name}
                  onChange={handleChange}
                  autoFocus
                />
                <label htmlFor="Abbreviation" className="form-label small fw-medium mb-1 mt-3">
                  Abbreviation
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. BCP (used in dense tables only)"
                  id="Abbreviation"
                  name="Abbreviation"
                  value={modalData.Abbreviation || ""}
                  onChange={handleChange}
                  maxLength={32}
                />
                <p className="small text-muted mb-0 mt-1">
                  Shown in Summary by Species and similar tables. Dropdowns and titles keep the full name.
                </p>
              </section>

              <section className="bg-white rounded-3 border p-3 mb-3">
                <h3 className="h6 text-uppercase text-muted fw-semibold mb-1" style={{ letterSpacing: "0.04em", fontSize: "0.7rem" }}>
                  Weight formulas
                </h3>
                <p className="small text-muted mb-3">
                  Use <code className="bg-light px-1 rounded">L</code> as the placeholder for length in each formula.
                </p>
                <div className="mb-3">
                  <label htmlFor="WsIP" className="form-label small fw-medium mb-1">
                    Ws (inch &amp; pound) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control font-monospace small"
                    placeholder="(L^3.345)/(10^3.576)"
                    id="WsIP"
                    name="WsIP"
                    value={modalData.WsIP}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-0">
                  <label htmlFor="WsMG" className="form-label small fw-medium mb-1">
                    Ws (mm &amp; gram) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control font-monospace small"
                    placeholder="(L^3.345)/(10^5.618)"
                    id="WsMG"
                    name="WsMG"
                    value={modalData.WsMG}
                    onChange={handleChange}
                  />
                </div>
              </section>

              <section className="bg-white rounded-3 border p-3 mb-3">
                <h3 className="h6 text-uppercase text-muted fw-semibold mb-1" style={{ letterSpacing: "0.04em", fontSize: "0.7rem" }}>
                  PSD thresholds
                </h3>
                <p className="small text-muted mb-3">
                  Optional. Provide all four inch/cm pairs, or leave all empty. Values must satisfy Quality &lt; Preferred &lt; Memorable &lt; Trophy.
                </p>
                <div className="row g-3">
                  {PSD_ROWS.map(([inKey, cmKey, label]) => (
                    <div className="col-sm-6" key={inKey}>
                      <label className="form-label small fw-medium text-secondary mb-1">{label}</label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light text-muted">in</span>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          id={inKey}
                          value={modalData[inKey] ?? ""}
                          onChange={handleChange}
                          placeholder="—"
                        />
                        <span className="input-group-text bg-light text-muted">cm</span>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          id={cmKey}
                          value={modalData[cmKey] ?? ""}
                          onChange={handleChange}
                          placeholder="—"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <label htmlFor="PsdReference" className="form-label small fw-medium mb-1">
                    PSD reference
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Source or notes (optional)"
                    id="PsdReference"
                    value={modalData.PsdReference ?? ""}
                    onChange={handleChange}
                  />
                </div>
              </section>

              <section className="bg-white rounded-3 border p-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="Status"
                    name="Status"
                    checked={modalData.Status}
                    onChange={handleChange}
                  />
                  <label className="form-check-label fw-medium" htmlFor="Status">
                    Active
                  </label>
                  <div className="form-text">Inactive types are hidden from fisher workflows.</div>
                </div>
              </section>
            </div>

            <div
              className="d-flex flex-wrap gap-2 justify-content-end px-4 py-3 border-top bg-white"
              style={{ flexShrink: 0 }}
            >
              <button type="button" className="btn btn-outline-secondary px-4" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary px-4">
                <i className={`fas ${isEditing ? "fa-check" : "fa-plus"} me-2`} aria-hidden />
                {isEditing ? "Save changes" : "Add fish type"}
              </button>
            </div>
          </form>
        </Modal>
      </div>
  );
};

export default FishTypes;